import type React from 'react'
import { useRef, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { RotateCcw, Save } from 'lucide-react'

interface SignaturePadProps {
  onComplete: (signatureData: File) => void
  id_client?: string
  id_user?: string
}

export default function SignaturePad({ onComplete, id_client, id_user }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false) // novo estado
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const preventPullToRefresh = (e: TouchEvent) => {
      if (window.scrollY === 0) e.preventDefault()
    }
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false })
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('touchmove', preventPullToRefresh)
    }
  }, [])

  useEffect(() => {
    const handleOrientationChange = () => {
      const isLandscapeMode =
        (screen.orientation?.type?.startsWith('landscape') ?? false) ||
        window.matchMedia('(orientation: landscape)').matches ||
        window.innerWidth > window.innerHeight

      setIsLandscape(isLandscapeMode)
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleOrientationChange)
    window.addEventListener('orientationchange', handleOrientationChange)
    handleOrientationChange()
    return () => {
      window.removeEventListener('resize', handleOrientationChange)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])

  useEffect(() => {
    const resizeCanvas = () => {
      if (canvasRef.current && containerRef.current) {
        const container = containerRef.current
        const rect = container.getBoundingClientRect()
        const dpr = window.devicePixelRatio || 1
        const availableWidth = rect.width - 32
        const availableHeight = rect.height - 32

        canvasRef.current.width = availableWidth * dpr
        canvasRef.current.height = availableHeight * dpr
        canvasRef.current.style.width = `${availableWidth}px`
        canvasRef.current.style.height = `${availableHeight}px`

        const ctx = canvasRef.current.getContext('2d')
        if (ctx) {
          ctx.scale(dpr, dpr)
          ctx.lineWidth = 2
          ctx.lineCap = 'round'
          ctx.strokeStyle = '#000'
        }
      }
    }
    const timeoutId = setTimeout(resizeCanvas, 100)
    window.addEventListener('resize', resizeCanvas)
    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', resizeCanvas)
    }
  }, [isLandscape, isMobile])

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true)
    setHasDrawn(true) // marca que já desenhou algo
    draw(e)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
    const ctx = canvasRef.current?.getContext('2d')
    ctx?.beginPath()
  }

  const getTouchOrMousePosition = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    const rect = canvas?.getBoundingClientRect()
    let x = 0,
      y = 0
    if (rect && canvas) {
      if ('touches' in e) {
        x = e.touches[0].clientX - rect.left
        y = e.touches[0].clientY - rect.top
      } else {
        x = e.clientX - rect.left
        y = e.clientY - rect.top
      }
    }
    return { x, y }
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) {
      const { x, y } = getTouchOrMousePosition(e)
      ctx.lineTo(x, y)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(x, y)
    }
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    setHasDrawn(false) // reset ao limpar
  }

  const saveSignature = () => {
    const canvas = canvasRef.current
    if (canvas) {
      canvas.toBlob(
        blob => {
          if (blob) {
            const file = new File([blob], `${id_client || id_user}_signature.png`, { type: 'image/png' })
            onComplete(file)
          }
        },
        'image/png',
        1
      )
    }
  }

  if (!isLandscape) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm mx-auto">
          <img src="/gire-tela.png" alt="Gire a tela" width={192} height={192} className="w-48 h-auto mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-4">Gire seu dispositivo</h2>
          <p className="text-foreground leading-relaxed">
            Para uma melhor experiência de assinatura, por favor gire seu dispositivo para a orientação paisagem.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className=" inset-0 h-screen w-screen sm:w-full bg-gradient-to-br">
      <div className="h-full flex flex-col w-full">
        <div className="bg-white shadow-lg p-2 flex-shrink-0">
          <div className="flex items-center justify-center gap-3">
            <div className="flex gap-2">
              <Button
                onClick={clearCanvas}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 bg-white hover:bg-gray-50 text-xs"
              >
                <RotateCcw className="w-3 h-3" />
                Limpar
              </Button>
              <h2 className="text-2xl font-bold mb-4 text-center">Assinatura Digital</h2>
              <Button
                onClick={saveSignature}
                size="sm"
                disabled={!hasDrawn} // só habilita se já desenhou
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-3 h-3" />
                Salvar
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col h-full w-full bg-white shadow-lg">
          <div ref={containerRef} className="flex-1 p-0 min-h-0">
            <div className="w-full h-full border border-gray-300 bg-white relative overflow-hidden">
              <canvas
                ref={canvasRef}
                className="absolute inset-0 bg-white cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseUp={stopDrawing}
                onMouseOut={stopDrawing}
                onMouseMove={draw}
                onTouchStart={startDrawing}
                onTouchEnd={stopDrawing}
                onTouchMove={draw}
                style={{ touchAction: 'none' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
